import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Media } from '@gripp/shared-logic';
import { Colors } from 'libs/shared-ui/src/themes';
import React from 'react';
import { Control, useController } from 'react-hook-form';
import {
  Linking,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { ImageData, useTypedMediaItem } from '../../media';
import { GetImageMediaComponent } from '../../media/getImageMediaComponent';

export type RenderMediaSourceAdminFormProps = {
  control: Control;
  fieldName: string;
};

export const RenderMediaSourceAdminForm: React.FC<
  RenderMediaSourceAdminFormProps
> = (props) => {
  const {
    field: { onChange, value },
  } = useController({
    control: props.control,
    name: props.fieldName,
  });

  if (!value) return null;

  const isLink = typeof value === 'string';

  if (isLink) {
    return <RenderLink value={value} onChange={onChange} />;
  }

  return <RenderImage value={value} onChange={onChange} />;
};

export const RenderMediaSourcExecutionForm: React.FC<{
  value: string | Media;
}> = ({ value }) => {
  if (!value) return null;

  const isLink = typeof value === 'string';

  if (isLink) {
    return <RenderLink value={value} showCloseButton={false} />;
  }

  return <RenderImage value={value} showCloseButton={false} />;
};

const RenderImage: React.FC<{
  value: Media;
  onChange?: (val: string) => void;
  showCloseButton?: boolean;
}> = ({ value, onChange, showCloseButton = true }) => {
  const { handleRefreshMediaItem } = useTypedMediaItem<ImageData>(value);

  if (!value) return null;

  return (
    <GetImageMediaComponent media={value} isAlignLeft={showCloseButton}>
      {showCloseButton ? (
        <Pressable
          onPress={() => {
            handleRefreshMediaItem();
            onChange?.('');
          }}
          style={styles.closeImageIcon}
        >
          <MaterialCommunityIcons
            name={'close'}
            color={Colors.white}
            size={16}
          />
        </Pressable>
      ) : null}
    </GetImageMediaComponent>
  );
};

const RenderLink = ({
  value,
  onChange,
  showCloseButton = true,
}: {
  value: string;
  onChange?: (newValue: string) => void;
  showCloseButton?: boolean;
}) => {
  const linkBox: StyleProp<ViewStyle> = [
    styles.linkBox,
    !showCloseButton && { justifyContent: 'center' as const },
  ];

  return (
    <View style={styles.linkContainer}>
      <Pressable onPress={() => Linking.openURL(value)} style={linkBox}>
        <MaterialCommunityIcons
          name="link-variant"
          size={20}
          color={Colors.primary}
        />
        <Text style={styles.linkText} numberOfLines={1}>
          {value}
        </Text>
      </Pressable>

      {showCloseButton && (
        <Pressable onPress={() => onChange?.('')} style={styles.closeIcon}>
          <MaterialCommunityIcons name="close" color={Colors.white} size={16} />
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  closeIcon: {
    position: 'absolute',
    top: 5,
    right: 5,
    backgroundColor: Colors.primary,
    borderRadius: 8,
    cursor: 'pointer',
    padding: 4,
  },
  linkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    padding: 10,
    position: 'relative',
  },
  linkBox: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  linkText: {
    color: Colors.primary,
    textDecorationLine: 'underline',
    flexShrink: 1,
  },
  closeImageIcon: {
    position: 'absolute',
    top: 5,
    right: 5,
    backgroundColor: Colors.primary,
    borderRadius: 8,
    cursor: 'pointer',
  },
});
